import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDeepCompareEffect } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableFooter from '@material-ui/core/TableFooter';
import Paper from '@material-ui/core/Paper';
import { emeaSummaryService } from 'reduxLib/services/EmeaServices/emeaSummaryService';
import { useEMEAGlobalContext } from 'context/EMEAGlobalContext';
import { useMultiSetQuery } from "customHooks/useQuery";
import { formatLocaleNumber } from 'helpers/emeaHelpers';

const useStyles = makeStyles((theme) => ({
  table: {
    borderRadius: theme.spacing(1)
  }
}));

export const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette?.background?.quaternaryBase,
    },
    '&:nth-of-type(even)': {
      backgroundColor: theme.palette?.background?.drawer,
    }
  }
}))(TableRow);

export const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.background?.primaryBase,
    color: theme.palette.white,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    fontSize: 14
  },
  body: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  footer: {
    backgroundColor: theme.palette?.table?.footer,
    color: theme.palette.white,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    textTransform: 'uppercase',
    borderBottom: `0px solid`
  }
}))(TableCell);

const EmeaTopMaterialSKUTable = (props) => {
  const {
    styles,
    subType,
    title,
    tableHeadTitle1,
    tableHeadTitle2,
    tableFooter,
    keyName,
    hyperlinkKey,
    hyperlinkType
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();
  const setMultiQuery = useMultiSetQuery();
  const dispatch = useDispatch();
  const [controller] = useState(() => new AbortController());
  const kpi_loading = useSelector(({ emeaSummaries }) => emeaSummaries?.[subType]?.loading);
  const kpi_data = useSelector(({ emeaSummaries }) => emeaSummaries?.[subType]?.data || []);
  const kpi_error = useSelector(({ emeaSummaries }) => emeaSummaries?.[subType]?.error);
  const [total, setTotal] = useState(0);
  
  const {
    locations,
    customers = [],
    sofcs = [],
    BU,
    documentDate
  } = useEMEAGlobalContext();

  const refreshSKUKPI = () => {
    const payload = {
      region: "EMEA",
      subtype: subType,
      businessUnits: BU,
      sites: locations,
      documentDate : documentDate,
      customerOrSalesOffice: [...customers, ...sofcs],
      currency: localStorage.getItem("currency") || "USD",
      orderType: "ORDER_ENTRY"
    }
    dispatch(emeaSummaryService({ payload, controller }));
  }

  const setHyperlinkFilters = ({ key, data, type }) => {
    setMultiQuery({
      emeaFilters: JSON.stringify({
          [key]: (type === "list") ? [data] : data
      }),
      isHyperlink: true
    });
  }

  useDeepCompareEffect(() => {
    refreshSKUKPI();
  }, [locations, sofcs, documentDate, customers, BU, localStorage.getItem("currency")]);

  useDeepCompareEffect(() => {
    if (!kpi_loading && kpi_data?.length) {
      const count = kpi_data.reduce((sum, curr) => sum + curr?.totalCount , 0);
      setTotal(count);
    } else {
      setTotal(0);
    }
  }, [kpi_data, kpi_loading])

  useEffect(() => {
    return () => {
      controller.abort();
    }
  }, []);

  return (
    <Box mt={3} className={styles.root} >
      <Typography variant="body1"> {title}  </Typography>
      <TableContainer component={Paper} className={styles.tableContainer}>
        <Table stickyHeader className={classes.table} aria-label="top materials sku table" data-testid="material-sku-table">
          <TableHead>
            <TableRow>
              <StyledTableCell> {tableHeadTitle1} </StyledTableCell>
              <StyledTableCell align="right"> {tableHeadTitle2} </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kpi_loading ? 
              <div className={styles.noData}> {t('please_wait_while_the_data_is_loading')} </div>
            : kpi_error ?
              <div className={styles.noData}> {t(kpi_error)} </div>
            : (kpi_data?.length === 0) ?
              <div className={styles.noData}> {t('no_data_found')} </div>
            : kpi_data.map((row) => (
              <StyledTableRow key={row[keyName]}>
                <StyledTableCell component="th" scope="row"> 
                  <Typography variant="body2" className={styles.hyperLink} onClick={() => setHyperlinkFilters({ key: hyperlinkKey, data: row[keyName], type: hyperlinkType })} data-testid={row[keyName]}> 
                    {row[keyName]} 
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align="right"> {formatLocaleNumber(row?.totalCount)} {row?.currency} </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
          {tableFooter == 'true' && (
          <TableFooter>
            <TableRow >
              <StyledTableCell> {t('total')} </StyledTableCell>
              <StyledTableCell align="right"> {formatLocaleNumber(total)} {localStorage.getItem("currency") || "USD"} 
              </StyledTableCell>
            </TableRow>
          </TableFooter>   
           )} 
        </Table>
      </TableContainer>
    </Box>
  )
}

export default EmeaTopMaterialSKUTable;